import { ref } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { useRoute } from "nuxt/app";
import { SEND_LEAD } from "./graphql/mutations.js";
import { formatLeadValue, generateConsentId, generateLeadId, getUtmParams } from "./utils.js";
import { optionalPhoneNumberSchema, requiredPhoneNumberSchema } from "./validator.js";
class FormDataCreator {
  subjects;
  consents;
  consentsValidation;
  clientId;
  formId;
  // Form Data
  inputsData;
  consentsData;
  selectorValue;
  // Form State
  errors;
  formSubmitted;
  isFormSent;
  isFormSuccess;
  isSubmitting;
  constructor(options) {
    this.subjects = options.subjects;
    this.consents = options.consents;
    this.consentsValidation = options.consentsValidation;
    this.clientId = options.clientId || "prometheus";
    this.formId = options.formId;
    this.inputsData = ref({});
    this.consentsData = ref({});
    this.selectorValue = ref("");
    this.errors = ref({});
    this.formSubmitted = ref(false);
    this.isFormSent = ref(false);
    this.isFormSuccess = ref(false);
    this.isSubmitting = ref(false);
    this.initializeFormData();
  }
  initializeFormData() {
    this.subjects.forEach((subject) => {
      if (!this.inputsData.value[subject.title]) {
        this.inputsData.value[subject.title] = {};
      }
      if (subject.selected || this.subjects.length === 1) {
        this.selectorValue.value = subject.title;
      }
      subject.inputs.forEach((input) => {
        this.inputsData.value[subject.title][input.label] = "";
      });
    });
  }
  setConsentsData(data) {
    this.consentsData.value = data;
  }
  getFormData() {
    return {
      inputsData: this.inputsData,
      selectorValue: this.selectorValue,
      formId: this.formId,
      formState: {
        errors: this.errors,
        isFormSent: this.isFormSent,
        isFormSuccess: this.isFormSuccess,
        formSubmitted: this.formSubmitted,
        isSubmitting: this.isSubmitting
      }
    };
  }
  async validateField(subjectTitle, inputLabel, index) {
    const input = this.subjects.find((s) => s.title === subjectTitle)?.inputs[index];
    if (!input?.validation) return;
    const inputId = `phone-input-${subjectTitle}-${index}`;
    const value = this.inputsData.value[subjectTitle][inputLabel];
    const validationResult = this.validatePhoneInput(value, input.validation);
    this.errors.value[inputId] = validationResult.isValid ? "" : validationResult.error || "";
  }
  validatePhoneInput(value, validation) {
    const schema = validation["phone.required"] ? requiredPhoneNumberSchema : optionalPhoneNumberSchema;
    const errorMessage = validation["phone.required"] ? validation["phone.required"] : validation["phone.invalid"];
    if (!value && !validation["phone.required"]) {
      return { isValid: true, errors: {}, error: "" };
    }
    const result = schema.safeParse(value);
    return {
      isValid: result.success,
      errors: {},
      error: result.success ? "" : result.error?.issues[0]?.message || errorMessage || ""
    };
  }
  validateConsentOnChange(name, parentName) {
    const consentName = parentName || name;
    const consent = this.consents.find((c) => c.name === consentName);
    const consentId = generateConsentId(this.formId, name, parentName);
    delete this.errors.value[consentId];
    if (consent?.consents) {
      consent.consents.forEach((el) => {
        delete this.errors.value[generateConsentId(this.formId, el.name, name)];
      });
    }
    const { isValid, errors } = this.validateConsents();
    if (!isValid) {
      Object.assign(this.errors.value, errors);
    }
  }
  validateConsents() {
    const errors = {};
    let isValid = true;
    Object.entries(this.consentsData.value).forEach(([name, value]) => {
      if (typeof value === "object") {
        Object.entries(value.consents).forEach(([subName, isChecked]) => {
          const consent = this.consents.find((c) => c.name === name);
          const subConsent = consent?.consents?.find((c) => c.name === subName);
          if (subConsent?.required && !isChecked) {
            const errorId = generateConsentId(this.formId, subName, name);
            errors[errorId] = this.consentsValidation["consent.required"] || "To pole jest wymagane";
            isValid = false;
          }
        });
      } else {
        const consent = this.consents.find((c) => c.name === name);
        if (consent?.required && !value) {
          const errorId = generateConsentId(this.formId, name);
          errors[errorId] = this.consentsValidation["consent.required"] || "To pole jest wymagane";
          isValid = false;
        }
      }
    });
    return { isValid, errors };
  }
  validateFormData(activeSubject, inputValues) {
    if (!activeSubject) return false;
    this.errors.value = {};
    let isValid = true;
    activeSubject.inputs.forEach((input, index) => {
      const value = inputValues[index];
      const inputId = `phone-input-${activeSubject.title}-${index}`;
      if (!input.validation) return;
      const validationResult = this.validatePhoneInput(value, input.validation);
      if (!validationResult.isValid) {
        this.errors.value[inputId] = validationResult.error || "";
        isValid = false;
      }
    });
    const consentsValidation = this.validateConsents();
    if (!consentsValidation.isValid) {
      Object.assign(this.errors.value, consentsValidation.errors);
      isValid = false;
    }
    return isValid;
  }
  buildFormFields(activeSubject, inputValues) {
    const route = useRoute();
    const [contactPhone, mainPhone] = inputValues;
    const fields = [
      { name: "SFID", value: formatLeadValue(activeSubject.SFID) },
      { name: "BAZA_SALES", value: formatLeadValue(activeSubject.BAZA_SALES) },
      { name: "ZDARZENIE", value: formatLeadValue(activeSubject.ZDARZENIE) },
      { name: "TYP", value: formatLeadValue(activeSubject.TYP) },
      { name: "OPIS", value: formatLeadValue(activeSubject.OPIS) },
      { name: "SOW", value: formatLeadValue("TAK") },
      { name: "FORM_URL", value: formatLeadValue(route.path) },
      { name: "LEAD_ID", value: formatLeadValue(generateLeadId()) }
    ];
    Object.entries(this.consentsData.value).forEach(([name, value]) => {
      if (typeof value === "object") {
        Object.entries(value.consents).forEach(([subName, isChecked]) => {
          fields.push({
            name: subName,
            value: formatLeadValue(isChecked ? "TAK" : "NIE")
          });
        });
      } else {
        fields.push({
          name,
          value: formatLeadValue(value ? "TAK" : "NIE")
        });
      }
    });
    if (contactPhone) {
      fields.push({
        name: "NR_KONTAKT",
        value: formatLeadValue(contactPhone).replace(/\s+/g, "")
      });
    }
    if (mainPhone) {
      fields.push({
        name: "NR_GLOWNY",
        value: formatLeadValue(mainPhone).replace(/\s+/g, "")
      });
    }
    const utmParams = getUtmParams();
    Object.entries(utmParams).forEach(([key, value]) => {
      fields.push({
        name: key,
        value: formatLeadValue(value)
      });
    });
    return fields;
  }
  async submitForm() {
    try {
      this.formSubmitted.value = true;
      this.isSubmitting.value = true;
      this.errors.value = {};
      const activeSubject = this.subjects.find((subject) => subject.title === this.selectorValue.value);
      const inputValues = Object.values(this.inputsData.value[this.selectorValue.value]);
      if (!activeSubject || !this.validateFormData(activeSubject, inputValues)) {
        this.isSubmitting.value = false;
        return false;
      }
      const fields = this.buildFormFields(activeSubject, inputValues);
      const { mutate } = useMutation(SEND_LEAD, {
        clientId: this.clientId
      });
      const result = await mutate({
        event_type: activeSubject.event_type,
        mail_title: activeSubject.mail_title,
        fields
      });
      this.isFormSuccess.value = !!result?.data?.sendLead;
      this.isFormSent.value = true;
      this.isSubmitting.value = false;
      return true;
    } catch (error) {
      this.isFormSuccess.value = false;
      this.isFormSent.value = true;
      this.isSubmitting.value = false;
      return false;
    }
  }
  handleRetry() {
    this.isFormSent.value = false;
    this.formSubmitted.value = false;
    this.errors.value = {};
  }
}
export default FormDataCreator;
